import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

import { BonusTitle, QuickBonus, Slider } from '@atlas/components';
import { AccountManager, useSettings } from '@atlas/core';
import { Icon, Label, ToggleCheckbox } from '@atlas/ui';

import { BASE_PATH, QUICK_DEPOSIT_POPUP_TITLE_TYPE } from 'src/common/constants';
import { CDN_TYPE_CRM, imageUrlCDN } from 'src/common/utils';
import { useBonusesContext } from 'src/components/BonusesProvider';
import { getMinDeposit, isAmountBelowMinDeposit } from 'src/components/BonusesProvider/utils';

const TITLE = {
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT]: 'quick_deposit_get_bonus',
  [QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT]: 'insufficient_funds_get_bonus',
};

const QuickDepositBonuses = ({
  currencyFormatter,
  isRtl = false,
  titleType = QUICK_DEPOSIT_POPUP_TITLE_TYPE.QUICK_DEPOSIT,
}) => {
  const { lang } = useSettings();
  const currency = AccountManager.getAccountCurrency();
  const {
    activatedBonus,
    amountToGetBonus,
    bonusesList,
    depositAmount,
    isLockedBonuses,
    selectedBonus,
    setSelectedBonus,
  } = useBonusesContext();

  const sliderKey = useMemo(() => bonusesList.map(({ id }) => id).join('-'), [bonusesList]);
  const dir = isRtl ? 'rtl' : 'ltr';
  const hasSingleBonus = bonusesList.length === 1;
  const [isSliderAtEnd, setIsSliderAtEnd] = useState(false);
  const titleMessage = TITLE[titleType] ?? TITLE[QUICK_DEPOSIT_POPUP_TITLE_TYPE.INSUFFICIENT];

  const handleSelectBonus = useCallback(
    bonus => {
      if (activatedBonus?.id === bonus?.id) {
        setSelectedBonus(bonus);
        return;
      }

      const isActive = selectedBonus?.id === bonus?.id;
      const selected = isActive ? null : bonus;
      setSelectedBonus(selected);
    },
    [activatedBonus, selectedBonus, setSelectedBonus],
  );

  const handleSliderProgress = useCallback((swiper) => {
    setIsSliderAtEnd(swiper.isEnd);
  }, []);

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      className: 'deposit-bonus__slider',
      dir,
      loop: false,
      nextBtn: <Slider.NextBtn className="deposit-bonus-slider__next-arrow" />,
      onSlideChange: handleSliderProgress,
      onSwiper: handleSliderProgress,
      prevBtn: <Slider.PrevBtn className="deposit-bonus-slider__prev-arrow" />,
      slidesPerView: 'auto',
      spaceBetween: 8,
    }),
    [dir, handleSliderProgress],
  );

  if (!bonusesList.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus?.financeDataList?.find(item => item.currency === currency);
    const image = bonus?.getImage(lang, 'ENG');

    const bonusImage = image
      ? `${imageUrlCDN(image, CDN_TYPE_CRM)}`
      : `${BASE_PATH}/static/assets/images/gift.svg`;
    const isActive = selectedBonus?.id === bonus?.id || activatedBonus?.id === bonus?.id;
    const isBelowMinDeposit = isAmountBelowMinDeposit(
      depositAmount,
      getMinDeposit(bonus, currency),
    );
    const bonusTitle = (
      <BonusTitle
        className="quick-deposit-bonus-title"
        name={bonus?.name}
        nameHighlighted={bonus?.nameHighlighted}
      />
    );

    const bonusDescription = (
      <div className="quick-deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: currency || bonus?.currency,
            minBonusAmount: finance?.minDeposit,
          }}
        />
      </div>
    );

    const disableBlock = (
      <div className="quick-deposit-bonus-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label className="disabled-title" message="deposit_bonus_unavaliable_message" />
      </div>
    );

    return (
      <div
        className={classNames('quick-deposit-bonus', {
          'quick-deposit-bonus--single': hasSingleBonus,
        })}
        key={bonus?.id}
        onClick={() => handleSelectBonus(bonus)}
      >
        <QuickBonus
          active={isActive}
          description={bonusDescription}
          disabled={bonus?.disabled || false}
          disabledBlock={disableBlock}
          image={bonusImage}
          title={bonusTitle}
          toggleCheckbox={
            <div className="quick-deposit-bonus-toggle">
              <ToggleCheckbox
                checked={isActive}
                disabled={isBelowMinDeposit || isLockedBonuses}
                onChange={() => handleSelectBonus(bonus)}
              />
            </div>
          }
        />
      </div>
    );
  };

  return (
    <>
      {amountToGetBonus > 0 && (
        <Label
          className="quick-deposit-bonus-error"
          message="quick_deposit_add_amount_to_get_bonus"
          params={{
            amount: currencyFormatter.format(amountToGetBonus),
          }}
        />
      )}

      <div
        className={classNames('quick-deposit-wrapper', {
          single: hasSingleBonus,
        })}
      >
        {!hasSingleBonus && <Label className="slider-title" message={titleMessage} />}
        {hasSingleBonus ? (
          renderBonus(bonusesList[0])
        ) : (
          <div
            className={classNames('slider-wrapper', {
              single: hasSingleBonus,
              'slider-wrapper--at-end': isSliderAtEnd,
            })}
          >
            <Slider key={sliderKey} {...sliderConfig}>
              {bonusesList.map(bonus => (
                <Slider.Slide className="quick-deposit-slide" key={bonus.id}>
                  {renderBonus(bonus)}
                </Slider.Slide>
              ))}
            </Slider>
          </div>
        )}
      </div>
    </>
  );
};

QuickDepositBonuses.propTypes = {
  currencyFormatter: PropTypes.shape({
    format: PropTypes.func.isRequired,
  }).isRequired,
  isRtl: PropTypes.bool,
  titleType: PropTypes.string,
};

export default QuickDepositBonuses;
